<template>
    <div class="main-card">
        <div class="card-header header-row">
            <span class="group-element">
                <span>
                    <i
                        :class="{ 'fas fa-vial': portfolio.mode === 'experiment', 'fas fa-vials': portfolio.mode === 'simulation' }"></i>
                </span>
                <span>
                    <div v-if="!isEditing" class="group-element">
                        <h4>{{ this.portfolio.name }}</h4>
                        <span @click="startNameEditing">
                            <i class="far fa-edit text-purple cursor-pointer"></i>
                        </span>
                    </div>
                    <div v-else class="group-element">
                        <input class="input-field" :class="{ 'border-error': hasError }" type="text"
                            v-model="editableName" />
                        <span @click="rename">
                            <i class="fas fa-save cursor-pointer"></i>
                        </span>
                    </div>
                </span>
            </span>
            <span class="text-blue cursor-pointer pull-right" @click="deletePortfolio">
                <i class="far fa-trash-alt"></i>
            </span>
        </div>
        <loading-container :loading="form.portfolioDetailLoading">
            <div class="card-body" v-if="portfolio">
                <div class="summary">
                    <p>{{ $t('common.daily_gain') }}
                        <span class="price-change box-150"
                            :class="{ 'positive': portfolio.dailyPercent > 0, 'negative': portfolio.dailyPercent < 0, 'neutral': portfolio.dailyPercent === 0 }">
                            <span>
                                <i
                                    :class="{ 'fas fa-arrow-up': portfolio.dailyPercent > 0, 'fas fa-arrow-down': portfolio.dailyPercent < 0, 'fas fa-minus': portfolio.dailyPercent === 0 }"></i>
                                {{ portfolio.dailyPercent | percentDisplay }}
                            </span>
                            <span>{{ portfolio.dailyGain | numberDisplay }} </span>
                        </span>
                    </p>
                    <p>{{ $t('common.cash') }}
                        <span>{{ portfolio.cash | numberDisplay }}</span>
                    </p>
                    <p>
                        {{ $t('common.total_invested_capital') }}
                        <span>{{ portfolio.totalInvestedCapital | numberDisplay }}</span>
                    </p>
                    <p>
                        {{ $t('common.gain') }}
                        <span class="price-change box-150"
                            :class="{ 'positive': portfolio.percentGain > 0, 'negative': portfolio.percentGain < 0, 'neutral': portfolio.percentGain === 0 }">

                            <span>
                                <i
                                    :class="{ 'fas fa-arrow-up': portfolio.percentGain > 0, 'fas fa-arrow-down': portfolio.percentGain < 0, 'fas fa-minus': portfolio.percentGain === 0 }"></i>
                                {{ portfolio.percentGain | percentDisplay }}
                            </span>
                            <span> {{ portfolio.gain | numberDisplay }}</span>
                        </span>
                        <!-- <span
                            :class="{ 'trans-positive': portfolio.gain > 0, 'trans-negative': portfolio.gain < 0, 'trans-neutral': portfolio.gain === 0 }">
                            {{ portfolio.gain | moneyDisplay }}
                        </span> -->
                    </p>
                    <p>
                        {{ $t('common.total_market_value') }}
                        <span>{{ portfolio.totalMarketValue | numberDisplay }}</span>
                    </p>
                </div>
                <div class="details">
                    <div class="header-row">
                        <h4>{{ $t('common.v_portfolio') }}</h4>
                        <div>
                            <FormButton iconClass="fas fa-chart-line" :text="$t('common.performance')"
                                :click="() => { showPerformanceChart = true }" class="btn btn-transparent" />
                            <FormButton iconClass="fas fa-plus" :text="$t('common.investment')"
                                :click="() => { showBuy = true }" class="btn btn-transparent" />
                        </div>
                    </div>
                    <div class="tbody">
                        <div v-if="portfolio.investments && portfolio.investments.length > 0">
                            <table class="table table-bordered">
                                <thead class="border-dark">
                                    <tr vertical-align="baseline">
                                        <th style="width: 20%">
                                            <p>{{ $t('stocks.code') }} </p>
                                            <p><span class="total-box">{{ portfolio.investments.length }}</span></p>
                                        </th>
                                        <th style="width: 20%">
                                            <p>{{ $t('common.qty') }}</p>
                                            <p>{{ $t('common.buy_price') }}</p>
                                        </th>
                                        <th style="width: 35%">
                                            <p>{{ $t('common.market_value') }}</p>
                                            <p>{{ $t('common.invested_capital') }}</p>
                                        </th>
                                        <th style="width: 20%">
                                            <p>{{ $t('common.gain') }} ({{ $t('units.million') }})</p>
                                            <p>{{ $t('common.gain') }} (%)</p>
                                        </th>
                                        <th>

                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style="height: 15px" v-for="row in portfolio.investments" :key="row.code">
                                        <td>
                                            <p><strong>{{ row.code }}</strong>
                                                <span class="hidden-xs-down">
                                                    | {{ row.startedDate | dateDisplay }}
                                                </span>
                                                <span class="text-blue pull-right medium-icon">
                                                    <span v-if="row.hasData" class="m-r-5"
                                                        @click="() => linkToStockInfo(row.code, true)">
                                                        <i class="mdi mdi-launch cursor-pointer"></i>
                                                    </span>
                                                    <span @click="() => displayCandleChart(row.code)">
                                                        <i class="fas fa-chart-line cursor-pointer"></i>
                                                    </span>
                                                </span>
                                            </p>
                                            <span class="price-change box-120"
                                                :class="{ 'ceil': row.marketPrice >= row.ceil, 'floor': row.marketPrice <= row.floor, 'positive': row.change > 0, 'negative': row.change < 0 && row.marketPrice > row.floor, 'neutral': row.change === 0 }">
                                                <span>
                                                    <i
                                                        :class="{ 'fas fa-arrow-up': row.change > 0, 'fas fa-arrow-down': row.change < 0, 'fas fa-minus': row.change === 0 }"></i>
                                                    {{ row.marketPrice | numberDisplay }}
                                                </span>
                                                <span>{{ row.change / 100 | percentDisplay }} </span>
                                            </span>
                                        </td>
                                        <td>
                                            <span class="pull-right">
                                                <p>{{ row.quantity | numberDisplay }}</p>
                                                <p>
                                                    {{ row.buyPrice | numberDisplay }}
                                                </p>
                                            </span>
                                        </td>
                                        <td>
                                            <span class="pull-right">
                                                <p>{{ row.marketValue | numberDisplay }}</p>
                                                <p>{{ row.investedCapital | numberDisplay }}</p>
                                            </span>
                                        </td>
                                        <td>
                                            <span class="pull-right"
                                                :class="{ 'trans-positive': row.gain > 0, 'trans-negative': row.gain < 0, 'trans-neutral': row.gain === 0 }">
                                                <p>{{ row.gain | shortMoneyDisplay }}</p>
                                                <p>{{ row.gainInPercent | percentDisplay }}</p>
                                            </span>
                                        </td>
                                        <td>
                                            <span class="text-blue cursor-pointer" @click="sellInvestment(row)">
                                                <i class="far fa-trash-alt"></i>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-else class="no-result-message">
                            {{ $t('messages.no_investments') }}
                        </div>
                    </div>
                </div>
                <Investment v-if="showBuy" mode="buy" :cash="portfolio.cash" :pid="portfolio.id" :pmode="portfolio.mode"
                    :soldStock="resetSoldCode" :pname="portfolio.name" @close="showBuy = false" />
                <Investment v-if="showSell" mode="sell" :soldStock="soldStock" :cash="portfolio.cash"
                    :pmode="portfolio.mode" :pid="portfolio.id" :pname="portfolio.name" @close="showSell = false" />

            </div>
            <div v-else class="row m-t-20 m-l-10">
                Could not load detail
            </div>
            <div v-if="showCandleChart === true">
                <CandleStick :symbol="symbol" @close="showCandleChart = false" />
            </div>
            <div v-if="showPerformanceChart === true">
                <PerformanceChart :pid="portfolio.id" :pname="portfolio.name" @close="showPerformanceChart = false" />
            </div>
        </loading-container>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LoadingContainer from "@/components/Misc/LoadingContainer";
import CreatePortfolio from './CreatePortfolio.vue';
import FormButton from '@/components/Form/FormButton';
import Investment from './Investment.vue'
import CandleStick from "../../../components/Chart/CandleStick.vue";
import PerformanceChart from "./PerformanceChart.vue";

export default {
    name: 'Portfolio',
    props: {
        id: String,
        name: String,
    },
    components: {
        LoadingContainer,
        CreatePortfolio,
        Investment,
        FormButton,
        CandleStick,
        PerformanceChart
    },
    computed: {
        ...mapGetters({
            form: "portfolio/form",
            portfolio: "portfolio/portfolio",
            performanceChart: "portfolio/performanceChart"
        })
    },
    data() {
        return {
            symbol: "",
            showCandleChart: false,
            showPerformanceChart: false,
            showBuy: false,
            showSell: false,
            isEditing: false,
            hasError: false,
            editableName: this.name,
            soldStock: {
                'code': '',
                'quantity': 0
            }
        };
    },
    methods: {
        ...mapActions({
            getPortfolioDetail: "portfolio/getPortfolioDetail",
            setDefaultCode: "configuration/setDefaultCode",
            removePortfolio: "home/removePortfolio",
            renamePortfolio: "portfolio/renamePortfolio"
        }),
        startNameEditing() {
            this.isEditing = true;
            this.editableName = this.portfolio.name;
        },
        rename() {
            if (this.editableName) {
                const params = {
                    'portfolioId': this.id,
                    'body': {
                        'name': this.editableName
                    }
                };
                this.portfolio['name'] = this.editableName;
                this.renamePortfolio(params);
                this.isEditing = false;
                this.hasError = false;
            } else {
                this.hasError = true;
            }
        },
        deletePortfolio() {
            this.$emit("deleted", this.id);
        },
        sellInvestment(stock) {
            this.soldStock = stock;
            this.showSell = true;
        },
        resetSoldCode() {
            this.soldStock['code'] = '';
        },
        displayCandleChart(symbol) {
            this.symbol = symbol;
            this.showCandleChart = true;
        },
        linkToStockInfo(code, isNewtab) {
            this.setDefaultCode(code);
            if (isNewtab) {
                let routeData = this.$router.resolve({ name: 'overview', params: { codeId: code } });
                window.open(routeData.href, '_blank');
            } else {
                this.$router.push({ name: 'overview', params: { codeId: code } }).catch(() => { });
            }
        },
    }
}
</script>

<style scoped>
.tbody {
    margin-top: 10px;
}

.header-row {
    display: flex;
    gap: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-row .group-element {
    gap: 10px;
    display: flex;
    align-items: center;
}

.input-field {
    border: 1px solid var(--gray);
    border-radius: 4px;
    font-size: 16px;
    padding: 5px;
    outline: none;
    -moz-appearance: textfield;
}

.total-box {
    border: 1px solid var(--gray);
    padding: 5px;
    border-radius: 4px;
}

.summary p {
    text-align: center;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.details p {
    margin-bottom: 5px;
}

@media only screen and (max-width: 768px) {

    table th,
    table td {
        font-size: 12px;
        padding: 5px;
    }
}

@media only screen and (max-width: 500px) {

    table th,
    table td {
        font-size: 12px;
        padding: 5px;
    }
}
</style>